import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  FaChevronDown,
  FaChevronRight,
  FaGears,
  FaUsers,
  FaCircleCheck,
  FaArrowUpRightFromSquare,
} from "react-icons/fa6";
import { FaExclamationTriangle } from "react-icons/fa";
import RefreshIndicator from "../Incidents/components/RefreshIndicator";
import usePagerDutyActivity from "../../../hooks/usePagerDutyActivity";
import { generatePagerDutyIncidentURL } from "../../../utils/pagerDutyUtils";
import PagerDutyTimelineList from "./PagerDutyTimelineList";
import PagerDutyEscalationGroupList from "./PagerDutyEscalationGroupList";
import styles from "./PagerDutyActivity.module.css";

/**
 * PagerDutyActivity — inline collapsible section showing PagerDuty timeline,
 * escalations, and incident-level responders.
 *
 * JSON contract (GET /api/v1/problem/{problem}/{outage}/pd-activity):
 *   { incidentId, available, fetchedAt, hostname,
 *     workflows:[{name,executedAt}],
 *     responders:[{id,name,status,at}],        ← flat list (feeds summary pills only)
 *     individuals:[{id,name,status,at}],       ← directly-paged responders
 *     timeline:[{type,label,at}],
 *     escalations:[{policyId,policyName,level,action,at,responders:[{id,name,status,at}]}],
 *     summary:{workflowCount,responderCount,respondedCount,timelineEventCount},
 *     reason }                                 ← only when available:false
 *   status values: notified | responded | escalated | declined
 *   reason values (available:false only): incident_not_found | fetch_failed |
 *     disabled | client_unavailable | no_incident_id. Machine-readable
 *     discriminators — the copy for each lives HERE, not in the API.
 *
 * Props:
 *   problemID {string|number}  problem.number
 *   outageID  {string|number}  outage.number
 *   isActive  {boolean}        false when incident is resolved (stops polling)
 */
function PagerDutyActivity({ problemID, outageID, isActive = true }) {
  const [expanded, setExpanded] = useState(false);

  const { data, loading, refreshing, lastUpdated, error } =
    usePagerDutyActivity(problemID, outageID, isActive);

  // Derive display values from the contract
  const available = data ? data.available !== false : null;

  // US2083214: `reason` explains an available:false payload. Only
  // "incident_not_found" means PagerDuty affirmatively has no such incident, i.e.
  // the stored id is wrong — the one case worth naming the value in the message.
  // Every other reason (fetch_failed, disabled, client_unavailable,
  // no_incident_id) is deliberately NOT treated as "invalid id": a PagerDuty
  // outage or a kill switch must never be reported to the MIM as a bad id, and an
  // older backend that sends no `reason` at all falls through to the generic copy.
  const incidentNotFound =
    !!data && data.reason === "incident_not_found" && !!data.incidentId;
  const responders = (data && data.responders) || [];
  const individuals = (data && data.individuals) || [];
  const timeline = (data && data.timeline) || [];
  const escalations = (data && data.escalations) || [];
  const summary = (data && data.summary) || {};

  const workflowCount = summary.workflowCount ?? (data && data.workflows ? data.workflows.length : 0);
  const responderCount = summary.responderCount ?? responders.length;
  const respondedCount = summary.respondedCount ?? 0;
  const timelineEventCount = summary.timelineEventCount ?? timeline.length;

  // Deep-link to PagerDuty incident. The hostname comes from the backend
  // response (per-environment), so the link is correct in every render context
  // without callers threading app config through.
  const pdUrl = data && data.incidentId
    ? generatePagerDutyIncidentURL(data.incidentId, { pagerduty: { hostname: data.hostname } })
    : null;

  // Collapsed summary pill text (unchanged)
  const summaryText =
    data && available
      ? `${workflowCount} workflow${workflowCount !== 1 ? "s" : ""} · ${responderCount} responder${responderCount !== 1 ? "s" : ""} · ${respondedCount} responded`
      : null;

  return (
    <div className={styles.container} data-testid="pd-activity-container">
      {/* Collapsible header — always visible */}
      <div className={styles.toggleHeader}>
        <button
          className={styles.toggleBtn}
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
          aria-controls="pd-activity-content"
        >
          <span className={styles.toggleLabel}>PagerDuty Activity</span>

          {/* Compact summary pills — only when collapsed and data is loaded */}
          {!expanded && !loading && data && available && (
            <span className={styles.summaryPills} aria-label={summaryText}>
              <span className={styles.pill}>
                <FaGears aria-hidden="true" />
                {workflowCount}
              </span>
              <span className={styles.pill}>
                <FaUsers aria-hidden="true" />
                {responderCount}
              </span>
              <span className={`${styles.pill} ${styles.pillResponded}`}>
                <FaCircleCheck aria-hidden="true" />
                {respondedCount}
              </span>
            </span>
          )}

          {/* Expand/collapse chevron — right edge of the toggle button */}
          <span className={styles.toggleIcon} aria-hidden="true">
            {expanded ? <FaChevronDown /> : <FaChevronRight />}
          </span>
        </button>

        {/* Refresh indicator — sibling of the toggle button (NOT a descendant),
            so its role="status" div is not invalid markup nested in a <button>. */}
        <span className={styles.refreshSlot}>
          <RefreshIndicator
            isRefreshing={refreshing}
            lastUpdated={lastUpdated}
            showTimestamp={!refreshing && !!lastUpdated && expanded}
            timestampFormat="relative"
          />
        </span>
      </div>

      {/* Invalid stored id — ALWAYS VISIBLE, deliberately OUTSIDE the {expanded}
          block below.
          The panel renders collapsed by default, so anything inside the expandable
          region is invisible until the user clicks the header. That is fine for
          activity detail, but not for "the PagerDuty id on this record is wrong":
          on the Page tab a MIM would see only a collapsed "PagerDuty Activity"
          header and no hint that paging cannot work. Surfacing it here means the
          warning is seen without a click, in both the Page tab and the Modern
          detail page, while leaving the user's expand/collapse state alone.
          Styled with errorState (bordered warning box) rather than the muted
          italic unavailableState, because this one is actionable. */}
      {!loading && !error && incidentNotFound && (
        <div className={styles.errorState} role="alert" data-testid="pd-incident-not-found">
          <FaExclamationTriangle className={styles.errorIcon} aria-hidden="true" />
          <span>No PagerDuty incident found for &quot;{data.incidentId}&quot;</span>
        </div>
      )}

      {/* Expandable content */}
      {expanded && (
        <div
          id="pd-activity-content"
          className={styles.content}
          role="region"
          aria-label="PagerDuty activity details"
        >
          {/* Loading state (initial load only) */}
          {loading && (
            <div className={styles.loadingState} aria-live="polite">
              <div className={styles.spinner} aria-hidden="true" />
              <span>Loading PagerDuty activity&hellip;</span>
            </div>
          )}

          {/* Error state */}
          {!loading && error && (
            <div className={styles.errorState} role="alert">
              <FaExclamationTriangle className={styles.errorIcon} aria-hidden="true" />
              <span>Unable to load PagerDuty activity. Data may be temporarily unavailable.</span>
            </div>
          )}

          {/* PD unavailable for any reason OTHER than a bad id. The bad-id case is
              rendered above, outside this collapsed-by-default region, so it is not
              repeated here. */}
          {!loading && !error && data && !available && !incidentNotFound && (
            <p className={styles.unavailableState}>
              PagerDuty activity unavailable for this incident.
            </p>
          )}

          {/* Main content — three sections in order */}
          {!loading && !error && data && available && (
            <>
              {/* Section 1: Deep-link to PagerDuty incident */}
              {pdUrl && (
                <div className={styles.deepLinkRow}>
                  <a
                    href={pdUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.deepLink}
                    aria-label={`View incident ${data.incidentId} in PagerDuty (opens in new tab)`}
                  >
                    <FaArrowUpRightFromSquare
                      className={styles.deepLinkIcon}
                      aria-hidden="true"
                    />
                    View incident {data.incidentId} in PagerDuty
                  </a>
                </div>
              )}

              {/* Section 2: Timeline */}
              <PagerDutyTimelineList
                timeline={timeline}
                timelineEventCount={timelineEventCount}
              />

              {/* Section 3: Responders — PagerDuty-style layout owned by
                  PagerDutyEscalationGroupList: "Responders" heading + expand/
                  collapse-all icon, escalation-policy groups listed directly,
                  then an Individuals labeled sub-block at the end. */}
              <PagerDutyEscalationGroupList
                escalations={escalations}
                individuals={individuals}
                respondedCount={respondedCount}
                responderCount={responderCount}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}

PagerDutyActivity.propTypes = {
  problemID: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  outageID: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  isActive: PropTypes.bool,
};

export default PagerDutyActivity;
